import { useEffect, useMemo, useRef, useState } from "react";
import type { SessionUser } from "../../shared/auth/access";
import type { ContractEditorSeed } from "./contract-types";
import { canDecideContract, contractEditorSeed, contractTotals } from "./contract-presentation";
import { useContractListWorkspace } from "./useContractListWorkspace";
import { useContractActionsWorkspace } from "./useContractActionsWorkspace";

export function useContractsWorkspace({ user, notify }: { user: SessionUser; notify: (message: string) => void }) {
  const scopeKey = `${user.id}:${user.role}`;
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;
  const list = useContractListWorkspace(scopeKey);
  const actions = useContractActionsWorkspace({ ...list, scopeKey, notify });
  const editorScope = useRef(scopeKey);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSeed, setEditorSeed] = useState<ContractEditorSeed | null>(null);
  useEffect(() => { setEditorOpen(false); setEditorSeed(null); }, [scopeKey]);
  const totals = useMemo(() => contractTotals(list.contracts, Date.now()), [list.contracts]);
  const canDecide = canDecideContract(user, list.detail);
  function openRevision() {
    if (!list.detail) return;
    try { editorScope.current = scopeKey; setEditorSeed(contractEditorSeed(list.detail)); setEditorOpen(true); }
    catch (caught) { notify(caught instanceof Error ? caught.message : "Sözleşme revizyonu hazırlanamadı."); }
  }
  async function onEditorSaved(saved: { id: string }) {
    if (currentScope.current !== scopeKey) return;
    setEditorOpen(false);
    list.setSelectedId(saved.id);
    notify(editorSeed ? "Yeni sözleşme sürümü kaydedildi." : "Sözleşme taslağı oluşturuldu.");
    await list.load();
  }
  return { ...list, ...actions, editorOpen: editorOpen && editorScope.current === scopeKey, editorSeed, totals, canDecide, openRevision, openNewContract: () => { editorScope.current = scopeKey; setEditorSeed(null); setEditorOpen(true); }, closeEditor: () => setEditorOpen(false), onEditorSaved };
}
